import React, { useEffect, useState, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useTheme } from "../../providers/ThemeContext";
import { useI18n } from "../../languages/providers/I18nProvider";
import { DrawerStyles } from "./DrawerStyles";
import { NavItem } from "../../types/RandomGlobalTypes";
import { useLangPath } from "../../hooks/useLangPath";
import BrandWordmark from "../brand/BrandWordmark";

type Props = {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  variant?: "default" | "eid55";
};

const MobileNavDrawer = ({
  open,
  onClose,
  items,
  variant = "default",
}: Props) => {
  const isEid = variant === "eid55";
  const [hovered, setHovered] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { theme, isDark } = useTheme();
  const { direction, t } = useI18n();
  const { pathname } = useLocation();
  const lp = useLangPath();
  const base = DrawerStyles(theme);
  const s = isEid
    ? {
        ...base,
        drawer: {
          ...base.drawer,
          backgroundColor: "#F5ECD4",
          color: "#2E3E5C",
        },
        drawerLink: { ...base.drawerLink, color: "#2E3E5C" },
        drawerLinkActive: { ...base.drawerLinkActive, color: "#2E3E5C" },
        ctaSecondary: { ...base.ctaSecondary, color: "#2E3E5C" },
      }
    : base;

  // Close the drawer whenever the route changes (e.g. tapping "Our Work"),
  // so it never lingers on top of the destination page.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // The drawer is anchored to the inline-end edge (right in LTR, left in RTL),
  // so the closed-state slide must move toward that same edge.
  const closedTransform =
    direction === "rtl" ? "translateX(-100%)" : "translateX(100%)";

  const hoverBg = isEid
    ? "rgba(46,62,92,0.08)"
    : isDark
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)";

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKeyDown);
    } else {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onKeyDown]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          ...s.drawerBackdrop,
          opacity: open ? 1 : 0,
          visibility: open ? "visible" : "hidden",
        }}
        aria-hidden={!open}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        style={{
          ...s.drawer,
          transform: open ? "translateX(0)" : closedTransform,
          // When closed, fully hide the off-screen panel so it can never peek
          // through or be reached by horizontal scrolling. Visibility flips
          // after the slide-out finishes so the close animation still plays.
          visibility: open ? "visible" : "hidden",
          pointerEvents: open ? "auto" : "none",
          transition: open
            ? "transform .3s ease, visibility 0s 0s"
            : "transform .3s ease, visibility 0s .3s",
        }}
      >
        {/* Header */}
        <div style={s.drawerHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <BrandWordmark isDark={isEid ? true : !isDark} />
          </div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              background: isEid
                ? "rgba(46,62,92,0.08)"
                : isDark
                  ? "none"
                  : `${theme.muted2}`,
              border:
                !isEid && isDark ? "2px solid rgba(71, 71, 71,1)" : "none",
              borderRadius: 10,
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isEid ? "#2E3E5C" : theme.text,
              cursor: "pointer",
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
        </div>

        {/* Links */}
        <div style={s.drawerList}>
          {items.map(({ label, href, children, highlight }) => {
            const dot = highlight ? (
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#B8293A",
                  display: "inline-block",
                  marginInlineEnd: 8,
                }}
              />
            ) : null;
            const link = (
              <NavLink
                to={href}
                end={!!children}
                onClick={onClose}
                onMouseEnter={() => setHovered(href)}
                onMouseLeave={() => setHovered(null)}
                style={({ isActive }) => ({
                  ...s.drawerLink,
                  ...(children ? { flex: 1 } : {}),
                  ...(hovered === href ? { backgroundColor: hoverBg } : {}),
                  ...(isActive ? s.drawerLinkActive : {}),
                })}
              >
                <span style={{ fontWeight: 500 }}>
                  {dot}
                  {label}
                </span>
              </NavLink>
            );
            if (!children) {
              return <React.Fragment key={href}>{link}</React.Fragment>;
            }
            const isOpen = expanded === href;
            const panelId = "drawer-sub-" + href.replace(/\W+/g, "-");
            return (
              <div key={href}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {link}
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : href)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    aria-label={label}
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      cursor: "pointer",
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ExpandMoreRoundedIcon
                      style={{
                        transition: "transform .2s ease",
                        transform: isOpen ? "rotate(180deg)" : "none",
                      }}
                    />
                  </button>
                </div>
                <div
                  id={panelId}
                  hidden={!isOpen}
                  style={{
                    display: isOpen ? "flex" : "none",
                    flexDirection: "column",
                    paddingInlineStart: 16,
                    gap: 2,
                  }}
                >
                  {children.map((c) => (
                    <NavLink
                      key={c.href}
                      to={c.href}
                      onClick={onClose}
                      style={{
                        ...s.drawerLink,
                        fontSize: 14,
                        padding: "0.6rem 0.75rem",
                        fontWeight: 400,
                      }}
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={s.drawerFooterWrap}>
          <div style={{ display: "grid", gap: 10 }}>
            <Link
              to={lp("/contact")}
              style={{ ...s.anchorBase, ...s.ctaPrimary }}
              onClick={onClose}
            >
              {t("common:getQuote")}
            </Link>

            <a
              href="https://wa.me/971554071164"
              target="_blank"
              rel="noreferrer"
              style={{ ...s.anchorBase, ...s.ctaSecondary, marginBottom: 20 }}
              onClick={onClose}
            >
              {t("common:chatWhatsApp")}
            </a>

            <div style={s.footerMetaRow}>
              <span>{t("footer:copyright")}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileNavDrawer;
